import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { Folder, FolderOpen, Plus, Pencil, Trash2, FolderInput } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { MediaFolderDocument } from '../../services/admin.mediaFolders.service'

interface MediaFolderTreeProps {
  folders: MediaFolderDocument[]
  selectedFolderId: string | null
  onSelect: (folderId: string | null) => void
  onCreate: (name: string, parentId: string | null) => Promise<void>
  onRename: (folderId: string, name: string) => Promise<void>
  onMove?: (folder: MediaFolderDocument) => void
  onDelete: (folderId: string) => Promise<void>
}

export function MediaFolderTree({
  folders,
  selectedFolderId,
  onSelect,
  onCreate,
  onRename,
  onMove,
  onDelete,
}: MediaFolderTreeProps) {
  const [creatingParent, setCreatingParent] = useState<string | 'root' | null>(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  async function handleCreate(parentId: string | null) {
    const name = newFolderName.trim()
    if (!name) { setCreatingParent(null); return }
    await onCreate(name, parentId)
    setCreatingParent(null)
    setNewFolderName('')
  }

  async function handleRename(folderId: string) {
    const name = renameValue.trim()
    if (!name) { setRenamingId(null); return }
    await onRename(folderId, name)
    setRenamingId(null)
    setRenameValue('')
  }

  async function handleDelete(folderId: string) {
    try {
      await onDelete(folderId)
    } catch (err) {
      alert(formatUserFacingError(err, 'Cannot delete folder.'))
    }
  }

  function renderCreateInput(parentId: string | null, depth: number) {
    return (
      <div
        key="new-folder-input"
        className="flex items-center gap-1 py-0.5"
        style={{ paddingLeft: `${(depth + 1) * 12 + 8}px` }}
      >
        <Input
          className="h-6 py-0 px-1 text-xs"
          placeholder="Folder name…"
          value={newFolderName}
          autoFocus
          onChange={(e) => setNewFolderName(e.target.value)}
          onBlur={() => void handleCreate(parentId)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleCreate(parentId)
            if (e.key === 'Escape') { setCreatingParent(null); setNewFolderName('') }
          }}
        />
      </div>
    )
  }

  function renderFolder(folder: MediaFolderDocument, depth = 0): React.ReactNode {
    const isSelected = selectedFolderId === folder.id
    const children = folders.filter((f) => f.parentId === folder.id)

    return (
      <div key={folder.id}>
        <div
          className={cn(
            'group flex items-center gap-1 rounded py-1 pr-1 text-sm transition-colors',
            isSelected ? 'bg-accent font-medium' : 'hover:bg-muted'
          )}
          style={{ paddingLeft: `${(depth + 1) * 12}px` }}
        >
          {isSelected ? (
            <FolderOpen className="h-4 w-4 shrink-0 text-brand-orange" />
          ) : (
            <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}

          {renamingId === folder.id ? (
            <Input
              className="h-6 py-0 px-1 text-xs flex-1"
              value={renameValue}
              autoFocus
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => void handleRename(folder.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleRename(folder.id)
                if (e.key === 'Escape') setRenamingId(null)
              }}
            />
          ) : (
            <button
              className="flex-1 truncate text-left"
              onClick={() => onSelect(folder.id)}
            >
              {folder.name}
            </button>
          )}

          <div className="ml-auto hidden items-center gap-0.5 group-hover:flex">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              title="Rename"
              onClick={(e) => {
                e.stopPropagation()
                setRenamingId(folder.id)
                setRenameValue(folder.name)
              }}
            >
              <Pencil className="h-3 w-3" />
            </Button>
            {onMove && (
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                title="Move"
                onClick={(e) => {
                  e.stopPropagation()
                  onMove(folder)
                }}
              >
                <FolderInput className="h-3 w-3" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-destructive hover:text-destructive"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation()
                void handleDelete(folder.id)
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              title="New subfolder"
              onClick={(e) => {
                e.stopPropagation()
                setCreatingParent(folder.id)
                setNewFolderName('')
              }}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {children.map((child) => renderFolder(child, depth + 1))}
        {creatingParent === folder.id && renderCreateInput(folder.id, depth + 1)}
      </div>
    )
  }

  const rootFolders = folders.filter((f) => f.parentId === null)

  return (
    <div className="flex flex-col gap-0.5 py-1">
      {/* Root / All Media entry */}
      <div
        className={cn(
          'group flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm font-medium transition-colors',
          selectedFolderId === null ? 'bg-accent' : 'hover:bg-muted'
        )}
        onClick={() => onSelect(null)}
      >
        {selectedFolderId === null ? (
          <FolderOpen className="h-4 w-4 shrink-0 text-brand-orange" />
        ) : (
          <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="flex-1">All Media</span>
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto h-5 w-5 hidden group-hover:flex"
          title="New folder"
          onClick={(e) => {
            e.stopPropagation()
            setCreatingParent('root')
            setNewFolderName('')
          }}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      {creatingParent === 'root' && renderCreateInput(null, 0)}
      {rootFolders.map((f) => renderFolder(f))}
    </div>
  )
}
